import React, { memo, useMemo } from "react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "utils";
import { Markdown } from "../../Markdown";
import { formatMarkdownSpaces } from "@/src/utils";

export interface ReasoningItem {
  id: string;
  text: string;
}

interface ThinkingReasoningListProps {
  items: ReasoningItem[];
  isExpanded: boolean;
  isLoading?: boolean;
}

const EXPAND_TRANSITION = {
  duration: 0.3,
  ease: [0.25, 0.1, 0.25, 1] as const,
};

const ITEM_VARIANTS = {
  hidden: { opacity: 0, y: -6 },
  visible: { opacity: 1, y: 0 },
};

const ThinkingReasoningList: React.FC<ThinkingReasoningListProps> = memo(
  ({ items, isExpanded, isLoading }) => {
    const visibleItems = useMemo(
      () => items.filter((item) => item.text?.trim()),
      [items],
    );

    if (visibleItems.length === 0) {
      return null;
    }

    return (
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            key="reasoning-list"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={EXPAND_TRANSITION}
            className="overflow-hidden"
            style={{ overflowAnchor: "none" }}
          >
            <div className="flex flex-col gap-0 pl-5 ml-[11px] border-l border-border_white_transparent20 mt-3 max-w-[calc(100vw-44px)]">
              {visibleItems.map((item, index) => {
                const isLast = index === visibleItems.length - 1;
                return (
                  <motion.div
                    key={item.id}
                    variants={ITEM_VARIANTS}
                    initial="hidden"
                    animate="visible"
                    transition={{ duration: 0.25, delay: index * 0.05 }}
                    className={cn({
                      "text-gradient-scroll": isLoading && isLast,
                    })}
                  >
                    <div className="body_m text-content_secondary leading-relaxed">
                      <Markdown>{formatMarkdownSpaces(item.text)}</Markdown>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
);

ThinkingReasoningList.displayName = "ThinkingReasoningList";

export default ThinkingReasoningList;
